import { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plant,
  Code,
  Fingerprint,
  FirstAidKit,
  Broadcast,
  Buildings,
  ChartLineUp,
  Cpu,
  CheckCircle,
} from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';

const services = [
  {
    id: 'smart-agriculture',
    icon: Plant,
    title: 'Smart Agriculture Systems',
    shortDesc: 'Intelligent farming solutions',
    description: 'Transform traditional farming with cutting-edge smart agriculture technology. Our solutions integrate IoT sensors, drone monitoring, precision irrigation, and data analytics to optimize crop yields, reduce resource waste, and enable data-driven farming decisions for sustainable agricultural practices.',
    features: ['IoT Sensor Networks', 'Drone Crop Monitoring', 'Precision Irrigation', 'Yield Analytics'],
    image: 'https://images.unsplash.com/photo-1651944432354-0dcd74b6c1bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=600&fit=crop',
  },
  {
    id: 'software',
    icon: Code,
    title: 'Software Development',
    shortDesc: 'Custom enterprise software',
    description: 'Build powerful, scalable software solutions tailored to your business requirements. From enterprise applications to consumer-facing products.',
    features: ['Custom Development', 'API Integration', 'Legacy Modernization', 'Quality Assurance'],
    image: 'https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop',
  },
  {
    id: 'biometric',
    icon: Fingerprint,
    title: 'Biometric Registration Systems',
    shortDesc: 'Advanced identity solutions',
    description: 'State-of-the-art biometric registration and identification systems including fingerprint scanning, facial recognition, and multi-factor authentication for secure identity management and access control.',
    features: ['Fingerprint Registration', 'Facial Recognition', 'Identity Management', 'Access Control'],
    image: 'https://customer-assets.emergentagent.com/job_atlas-enterprise-hub/artifacts/xnacgtru_Biometric%20Atlas.png',
  },
  {
    id: 'health',
    icon: FirstAidKit,
    title: 'Health App Solutions',
    shortDesc: 'HIPAA-compliant digital health',
    description: 'Specialized healthcare technology solutions built with HIPAA compliance at the core. Empower your healthcare organization with secure digital tools.',
    features: ['HIPAA Compliance', 'EHR Integration', 'Telehealth Platforms', 'Patient Portals'],
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=600&fit=crop',
  },
  {
    id: 'satellite',
    icon: Broadcast,
    title: 'Satellite Services',
    shortDesc: 'Remote connectivity solutions',
    description: 'Reliable satellite connectivity and data solutions for operations in remote locations. Stay connected anywhere on the planet.',
    features: ['VSAT Solutions', 'Maritime Connectivity', 'Remote Operations', 'Backup Communications'],
    image: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=600&fit=crop',
  },
  {
    id: 'workspace',
    icon: Buildings,
    title: 'Advanced Workspace Management',
    shortDesc: 'AI-driven hybrid work solutions',
    description: 'Intelligent workspace management software for the modern hybrid work environment. Optimize space utilization and employee experience.',
    features: ['Space Booking', 'Occupancy Analytics', 'Visitor Management', 'IoT Integration'],
    image: 'https://images.pexels.com/photos/416320/pexels-photo-416320.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop',
  },
  {
    id: 'pm-consultancy',
    icon: ChartLineUp,
    title: 'Project Management Consultancy',
    shortDesc: 'Expert PM guidance',
    description: 'Strategic project management consultancy to ensure your technology initiatives are delivered on time and within budget.',
    features: ['Agile Transformation', 'PMO Setup', 'Resource Planning', 'Risk Management'],
    image: 'https://images.pexels.com/photos/2977547/pexels-photo-2977547.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop',
  },
  {
    id: 'it-architecture',
    icon: Cpu,
    title: 'IT Architecture Configuration',
    shortDesc: 'Enterprise architecture design',
    description: 'Design and implement robust IT architectures that scale with your business. From cloud-native to hybrid environments.',
    features: ['Architecture Design', 'Cloud Strategy', 'Security Architecture', 'Performance Optimization'],
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=600&fit=crop',
  },
];

export default function SolutionsPage() {
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="solutions-page" className={`min-h-screen pt-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'}`}>
      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <span className="text-[#00C8EB] font-medium uppercase tracking-wider text-sm">
            Our Solutions
          </span>
          <h1 className={`text-4xl md:text-5xl font-bold mt-3 mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Intelligent Technology{' '}
            <span className="text-gradient">Solutions</span>
          </h1>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Comprehensive technology services designed to transform your business operations 
            and drive innovation at scale.
          </p>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24">
        <div className="space-y-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              id={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.05 }}
              data-testid={`solution-card-${service.id}`}
              className={`group overflow-hidden rounded-2xl ${
                theme === 'dark'
                  ? 'bg-[#12131F] border border-[#464B8B]/20'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                {/* Image */}
                <div className="lg:w-1/2 h-64 lg:h-80 relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent lg:hidden" />
                </div>

                {/* Content */}
                <div className="lg:w-1/2 p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center flex-shrink-0">
                      <service.icon size={28} weight="duotone" className="text-[#00C8EB]" />
                    </div>
                    <h3 className={`font-bold text-2xl ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {service.title}
                    </h3>
                  </div>

                  <p className={`mb-6 text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {service.description}
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2">
                        <CheckCircle size={18} weight="duotone" className="text-[#00C8EB] flex-shrink-0" />
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`py-24 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Need a Custom Solution?
            </h2>
            <p className={`text-lg mb-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Our team of experts can design and implement tailored technology solutions 
              that perfectly fit your unique business requirements.
            </p>
            <Link to="/contact">
              <Button
                data-testid="solutions-cta-btn"
                size="lg"
                className="bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10] font-semibold px-8"
              >
                Contact Our Team
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
